import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Palette, FloppyDisk, ArrowCounterClockwise } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BrandingPanel = () => {
  const { t } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    brand_name: '',
    brand_logo_url: '',
    brand_primary_color: '',
    brand_footer_text: '',
  });

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const res = await axios.get(`${API_URL}/branding`, { headers: getAuthHeaders() });
        setForm({
          brand_name: res.data.brand_name || '',
          brand_logo_url: res.data.brand_logo_url || '',
          brand_primary_color: res.data.brand_primary_color || '',
          brand_footer_text: res.data.brand_footer_text || '',
        });
      } catch (err) {
        toast.error('Failed to load branding');
      } finally {
        setLoading(false);
      }
    };
    fetchBranding();
  }, [getAuthHeaders]);

  const handleSave = async () => {
    if (form.brand_primary_color && !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(form.brand_primary_color.trim())) {
      toast.error(t('branding.invalidColor'));
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/branding`, form, { headers: getAuthHeaders() });
      toast.success(t('branding.saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setForm({ brand_name: '', brand_logo_url: '', brand_primary_color: '', brand_footer_text: '' });
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/branding`,
        { brand_name: '', brand_logo_url: '', brand_primary_color: '', brand_footer_text: '' },
        { headers: getAuthHeaders() }
      );
      toast.success(t('branding.saved'));
    } catch (err) {
      toast.error('Reset failed');
    } finally {
      setSaving(false);
    }
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const effectiveColor = form.brand_primary_color && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(form.brand_primary_color.trim())
    ? form.brand_primary_color
    : '#0F1B36';
  const effectiveName = form.brand_name || 'GrowthRIQ';

  if (loading) return <div className="p-8 text-center text-[#52525B]">{t('common.loading')}</div>;

  return (
    <div className="bg-white border border-[#E4E4E7]" data-testid="branding-panel">
      <div className="flex items-center justify-between p-4 border-b border-[#E4E4E7]">
        <div className="flex items-center gap-2">
          <Palette size={20} className="text-[#0F1B36]" />
          <div>
            <h3 className="text-lg font-semibold text-[#09090B]">{t('branding.title')}</h3>
            <p className="text-xs text-[#52525B]">{t('branding.subtitle')}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleReset}
            variant="outline"
            disabled={saving}
            className="rounded-none"
            data-testid="branding-reset"
          >
            <ArrowCounterClockwise size={14} className="mr-2" />
            {t('branding.reset')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#0F1B36] hover:bg-[#0F1B36]/90 text-white rounded-none"
            data-testid="branding-save"
          >
            <FloppyDisk size={14} className="mr-2" />
            {saving ? t('common.loading') : t('branding.save')}
          </Button>
        </div>
      </div>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="brand_name">{t('branding.brand_name')}</Label>
            <Input
              id="brand_name"
              value={form.brand_name}
              onChange={(e) => update('brand_name', e.target.value)}
              placeholder="GrowthRIQ"
              className="rounded-none"
              maxLength={255}
              data-testid="branding-name"
            />
            <p className="text-xs text-[#52525B] mt-1">{t('branding.brand_name.help')}</p>
          </div>

          <div>
            <Label htmlFor="logo_url">{t('branding.logo_url')}</Label>
            <Input
              id="logo_url"
              value={form.brand_logo_url}
              onChange={(e) => update('brand_logo_url', e.target.value)}
              placeholder="https://cdn.acme.com/logo.png"
              className="rounded-none"
              maxLength={500}
              data-testid="branding-logo"
            />
            <p className="text-xs text-[#52525B] mt-1">{t('branding.logo_url.help')}</p>
          </div>

          <div>
            <Label htmlFor="primary_color">{t('branding.primary_color')}</Label>
            <div className="flex gap-2 items-center">
              <Input
                id="primary_color"
                value={form.brand_primary_color}
                onChange={(e) => update('brand_primary_color', e.target.value)}
                placeholder="#1E40AF"
                className="rounded-none font-mono uppercase"
                maxLength={7}
                data-testid="branding-color"
              />
              <input
                type="color"
                value={effectiveColor}
                onChange={(e) => update('brand_primary_color', e.target.value.toUpperCase())}
                className="h-10 w-12 border border-[#E4E4E7] cursor-pointer"
                data-testid="branding-color-picker"
              />
            </div>
            <p className="text-xs text-[#52525B] mt-1">{t('branding.primary_color.help')}</p>
          </div>

          <div>
            <Label htmlFor="footer_text">{t('branding.footer_text')}</Label>
            <Textarea
              id="footer_text"
              value={form.brand_footer_text}
              onChange={(e) => update('brand_footer_text', e.target.value)}
              placeholder="© 2026 Acme Corp. Confidential."
              className="rounded-none min-h-[90px]"
              maxLength={500}
              data-testid="branding-footer"
            />
            <p className="text-xs text-[#52525B] mt-1">{t('branding.footer_text.help')}</p>
          </div>
        </div>

        <div className="space-y-3">
          <Label>{t('branding.preview')}</Label>
          <div
            className="border border-[#E4E4E7] bg-[#F4F4F5] p-12 flex flex-col items-center justify-center gap-4 min-h-[400px]"
            style={{ borderTopColor: effectiveColor, borderTopWidth: '4px' }}
            data-testid="branding-preview"
          >
            {form.brand_logo_url ? (
              <img
                src={form.brand_logo_url}
                alt={effectiveName}
                className="max-h-20 max-w-[260px] object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="text-3xl font-extrabold" style={{ color: effectiveColor }}>
                {effectiveName}
              </div>
            )}
            <div className="text-center">
              <div className="text-xs uppercase tracking-wider text-[#52525B] mb-1">Section 01</div>
              <h4 className="text-2xl font-bold text-[#09090B]" style={{ borderBottom: `3px solid ${effectiveColor}`, paddingBottom: '4px' }}>
                Executive Summary
              </h4>
            </div>
            <p className="text-sm text-[#52525B] text-center max-w-xs italic">
              "Your brand will appear on cover, section headers and back cover of every generated PDF report."
            </p>
            {form.brand_footer_text && (
              <div className="mt-auto pt-6 border-t border-[#E4E4E7] w-full text-center">
                <p className="text-xs text-[#52525B] whitespace-pre-line">{form.brand_footer_text}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandingPanel;
